/* eslint-disable no-console */
import axios, {AxiosError} from 'axios';

import {API_BASE_URL, API_ENDPOINT_JSON} from '../../../config';
import CRUDMethods from '../../../constants/CRUDConstants';
import useUserStore from '../../../store/userStore/userStore';
import type {
  PCFRecord,
  PCFRecordInput,
  PCFRecordPayload,
  PCFRecordUpdatePayload
} from '../../interface/pcfTypes';

interface ErrorResponse {
  message: string;
}

interface DeleteResult {
  status: string;
  message?: string;
}

export interface PCFValidateCheck {
  key: string;
  value: string;
}

export interface PCFValidateRequest {
  company: string;
  excludeId?: string;
  checks: PCFValidateCheck[];
}

export interface PCFValidateResult {
  key: string;
  valid: boolean;
  message?: string;
}

export interface PCFValidateResponse {
  status: string;
  message: string;
  data: {
    valid: boolean;
    results: PCFValidateResult[];
  };
}

const authHeaders = () => ({
  Authorization: `Bearer ${useUserStore.getState().userToken}`
});

const baseUrl = `${API_BASE_URL}${API_ENDPOINT_JSON.pcf}`;

const getUserId = (): string => useUserStore.getState().userDetails?._id ?? '';

const buildCreatePayload = (input: PCFRecordInput): PCFRecordPayload => {
  const userId = getUserId();

  return {
    ...input,
    createdBy: userId,
    updatedBy: userId
  };
};

const buildUpdatePayload = (
  input: PCFRecordInput,
  createdBy: string
): PCFRecordUpdatePayload => ({
  ...input,
  createdBy,
  updatedBy: getUserId()
});

const unwrapRecord = (response: {
  data: {data?: PCFRecord} & PCFRecord;
}): PCFRecord => {
  const record = response.data.data ?? response.data;
  const resolvedId =
    record._id ?? (record as PCFRecord & {id?: string}).id;

  if (resolvedId && !record._id) {
    return {...record, _id: resolvedId};
  }

  return record;
};

const unwrapRecords = (response: {
  data: {data?: PCFRecord[]} & PCFRecord[];
}): PCFRecord[] => {
  const payload = response.data.data ?? response.data;

  return Array.isArray(payload) ? payload : [];
};

export const createPCFRecord = async (
  input: PCFRecordInput
): Promise<PCFRecord> => {
  try {
    const response = await axios.request<{data?: PCFRecord} & PCFRecord>({
      method: CRUDMethods.POST,
      url: baseUrl,
      headers: authHeaders(),
      params: {},
      data: buildCreatePayload(input)
    });

    return unwrapRecord(response);
  } catch (err) {
    console.log('err:', err);
    throw err;
  }
};

export const listPCFRecords = async (company: string): Promise<PCFRecord[]> => {
  try {
    const response = await axios.request<{data?: PCFRecord[]} & PCFRecord[]>({
      method: CRUDMethods.GET,
      url: `${baseUrl}/${company}`,
      headers: authHeaders(),
      params: {},
      data: {}
    });

    return unwrapRecords(response);
  } catch (err) {
    console.log('err:', err);
    throw err;
  }
};

export const getPCFRecord = async (id: string): Promise<PCFRecord> => {
  try {
    const response = await axios.request<{data?: PCFRecord} & PCFRecord>({
      method: CRUDMethods.GET,
      url: `${baseUrl}/record/${id}`,
      headers: authHeaders(),
      params: {},
      data: {}
    });

    return unwrapRecord(response);
  } catch (err) {
    console.log('err:', err);
    throw err;
  }
};

export const updatePCFRecord = async (
  id: string,
  input: PCFRecordInput,
  createdBy: string
): Promise<PCFRecord> => {
  try {
    const response = await axios.request<{data?: PCFRecord} & PCFRecord>({
      method: CRUDMethods.PUT,
      url: `${baseUrl}/${id}`,
      headers: authHeaders(),
      params: {},
      data: buildUpdatePayload(input, createdBy)
    });

    return unwrapRecord(response);
  } catch (err) {
    console.log('err:', err);
    throw err;
  }
};

export const validatePCF = async (
  input: PCFValidateRequest
): Promise<PCFValidateResponse> => {
  try {
    const response = await axios.request<
      {data?: PCFValidateResponse} & PCFValidateResponse
    >({
      method: CRUDMethods.POST,
      url: `${baseUrl}/validate`,
      headers: authHeaders(),
      params: {},
      data: input
    });

    return response.data as PCFValidateResponse;
  } catch (err) {
    console.log('err:', err);
    throw err;
  }
};

export const deletePCFRecord = async (id: string): Promise<DeleteResult> => {
  try {
    const response = await axios.request({
      method: CRUDMethods.DELETE,
      url: `${baseUrl}/${id}`,
      headers: authHeaders()
    });

    return response.data;
  } catch (err) {
    const axiosError = err as AxiosError;
    if (axiosError.response) {
      const responseData = axiosError.response.data as ErrorResponse;

      return {
        status: 'error',
        message: responseData.message || 'An error occurred'
      };
    }

    return {
      status: 'error',
      message: axiosError.message || 'An error occurred'
    };
  }
};
